import { useState, useEffect, useRef, useId } from 'react';
import { GoogleLogin, useGoogleOAuth } from '@react-oauth/google';
import { Lock, Shield, Clock, LogOut, ChevronDown, AlertTriangle, UserRound } from 'lucide-react';
import { useTheme } from 'next-themes';

import { Card, CardContent, CardHeader } from '../ui/card';
import { markLoginSuccess } from '../../services/adminApi';
import { cacheAdminPreferences } from '../../services/adminPreferences';
import {
  SIGN_IN_GATES,
  closeSignInGate,
  disableGoogleAutoSelect,
  forgetRememberedAccount,
  getRememberedAccount,
  getSignInGate,
  openSignInGate,
  rememberAccount,
} from '../../services/adminSignInPreferences';

// How long to wait for the Google Identity Services script before offering
// the unavailable state. The button still appears if the script loads later.
export const GOOGLE_UNAVAILABLE_AFTER_MS = 8000;

const SESSION_MESSAGES = {
  inactivity: {
    title: 'Session ended',
    body: 'You were signed out after a period of inactivity. Sign in again to continue.',
    icon: Clock,
  },
  expired: {
    title: 'Session ended',
    body: 'Your session has expired. Sign in again to continue.',
    icon: Clock,
  },
  access_denied: {
    title: 'Access not authorized',
    body: 'This account no longer has access to Owaza Admin. Contact your system administrator if you need access.',
    icon: Lock,
    tone: 'danger',
  },
  signed_out: {
    title: 'Signed out',
    body: 'You have signed out. Sign in again whenever you are ready.',
    icon: LogOut,
    tone: 'neutral',
  },
};

const NOTICE_TONES = {
  neutral: 'bg-gray-50 dark:bg-gray-900/40 border-gray-300 dark:border-gray-600 text-gray-800 dark:text-gray-200',
  warning: 'bg-amber-50 dark:bg-amber-900/30 border-amber-500 text-amber-900 dark:text-amber-100',
  danger: 'bg-red-50 dark:bg-red-900/30 border-red-500 text-red-800 dark:text-red-100',
};

const Notice = ({ tone, icon: Icon, title, children, role = 'status', testId }) => (
  <div
    role={role}
    data-testid={testId}
    className={`border-l-4 p-4 rounded ${NOTICE_TONES[tone]}`}
  >
    <div className="flex items-start">
      <Icon className="h-5 w-5 mr-2 mt-0.5 flex-shrink-0" aria-hidden="true" />
      <div>
        <p className="font-semibold">{title}</p>
        <div className="text-sm mt-1">{children}</div>
      </div>
    </div>
  </div>
);

const describeAuthFailure = (response, data, fallback) => {
  if (response.status === 401 || response.status === 403) {
    return { title: 'Access not authorized', body: data?.detail || fallback };
  }
  return {
    title: 'Sign-in could not be completed',
    body: data?.detail || 'Something went wrong on our side. Please try again in a moment.',
  };
};

const NETWORK_FAILURE = {
  title: 'Sign-in could not be completed',
  body: 'We could not reach the Owaza server. Check your connection and try again.',
};

const readJson = async (response) => {
  try {
    return await response.json();
  } catch {
    return null;
  }
};

const AdminLogin = ({ onLogin, logoutReason }) => {
  const { setTheme } = useTheme();
  const { clientId, scriptLoadedSuccessfully } = useGoogleOAuth();
  const idPrefix = useId();
  const usernameId = `${idPrefix}-username`;
  const passwordId = `${idPrefix}-password`;
  const alternatePanelId = `${idPrefix}-alternate`;
  const rememberId = `${idPrefix}-remember`;
  const googleHeadingId = `${idPrefix}-google-heading`;

  const [rememberedAccount, setRememberedAccount] = useState(getRememberedAccount);
  const [rememberChoice, setRememberChoice] = useState(() => getRememberedAccount() !== null);
  const [signInGate, setSignInGate] = useState(getSignInGate);
  // Once Google has shown (or declined to show) its prompt on this visit, never
  // prompt again: remounting the button after an error must not loop.
  const [promptSpent, setPromptSpent] = useState(false);
  const [promptStatus, setPromptStatus] = useState('idle');
  const [googleTimedOut, setGoogleTimedOut] = useState(false);
  const [googleLoading, setGoogleLoading] = useState(false);
  const [googleError, setGoogleError] = useState(null);
  const [accountNotice, setAccountNotice] = useState('');
  const [sessionReason, setSessionReason] = useState(logoutReason || null);

  const [alternateOpen, setAlternateOpen] = useState(false);
  const [localUsername, setLocalUsername] = useState('');
  const [localPassword, setLocalPassword] = useState('');
  const [fieldErrors, setFieldErrors] = useState({});
  const [passwordLoading, setPasswordLoading] = useState(false);
  const [passwordError, setPasswordError] = useState(null);

  // One sign-in at a time across Google and password: a credential that
  // arrives while another attempt is verifying is ignored, so it can never
  // overwrite the session the admin chose.
  const authInFlightRef = useRef(false);
  const mountedRef = useRef(true);
  const usernameRef = useRef(null);
  const passwordRef = useRef(null);

  useEffect(() => {
    mountedRef.current = true;
    return () => { mountedRef.current = false; };
  }, []);

  useEffect(() => {
    setSessionReason(logoutReason || null);
  }, [logoutReason]);

  // Belt and braces: whenever this device is gated, also tell Google not to
  // auto-select, in case its script was not loaded when the session ended.
  useEffect(() => {
    if (signInGate && scriptLoadedSuccessfully) disableGoogleAutoSelect();
  }, [signInGate, scriptLoadedSuccessfully]);

  useEffect(() => {
    if (scriptLoadedSuccessfully) {
      setGoogleTimedOut(false);
      return undefined;
    }
    const timer = setTimeout(() => setGoogleTimedOut(true), GOOGLE_UNAVAILABLE_AFTER_MS);
    return () => clearTimeout(timer);
  }, [scriptLoadedSuccessfully]);

  useEffect(() => {
    if (!alternateOpen) return;
    // Choosing the alternate path cancels any automatic Google continuation.
    setPromptSpent(true);
    usernameRef.current?.focus();
  }, [alternateOpen]);

  const googleUnavailable = !clientId || (!scriptLoadedSuccessfully && googleTimedOut);
  const busy = googleLoading || passwordLoading;

  // Google continuation policy (OWZ-80):
  // - no remembered account, a deliberate sign-out, an access rejection, or an
  //   account switch: the plain Google button only;
  // - an in-app session expiry: Google's prompt, but the user must confirm the
  //   account (no auto-select);
  // - otherwise: the prompt with auto-select, so a returning admin on a trusted
  //   device can continue without an unnecessary chooser when Google allows it.
  // Google and the browser still decide whether any prompt is shown, and the
  // backend still authorizes every credential.
  const promptEnabled = Boolean(rememberedAccount)
    && signInGate !== SIGN_IN_GATES.SIGNED_OUT
    && !promptSpent;
  const autoSelect = promptEnabled && signInGate === null;
  const loginHint = rememberedAccount?.email;

  const completeLogin = (data) => {
    if (!mountedRef.current) return false;
    localStorage.setItem('googleAuthToken', data.token);
    localStorage.setItem('adminUser', JSON.stringify(data.user));
    cacheAdminPreferences(data.user.preferences, data.user);
    setTheme(data.user.preferences?.appearance || 'system');
    openSignInGate();
    // Mark login success to prevent stale 401s from triggering logout
    markLoginSuccess();
    onLogin(true);
    return true;
  };

  // Never let Google continue automatically after any rejected sign-in.
  const gateAfterRejection = () => {
    closeSignInGate(SIGN_IN_GATES.SIGNED_OUT);
    setSignInGate(SIGN_IN_GATES.SIGNED_OUT);
  };

  const handleGoogleSuccess = async (credentialResponse) => {
    // The prompt and the button can both deliver a credential; verify only one.
    if (authInFlightRef.current) return;
    authInFlightRef.current = true;
    setPromptSpent(true);
    setGoogleLoading(true);
    setGoogleError(null);
    setPasswordError(null);
    setAccountNotice('');
    setSessionReason(null);

    try {
      // The backend verifies the Google credential and the admin record and
      // issues the Owaza session; only that session token is stored.
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/auth/google`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          credential: credentialResponse.credential
        })
      });
      const data = await readJson(response);

      if (!mountedRef.current) return;
      if (response.ok && data?.token && data?.user) {
        if (rememberChoice) rememberAccount(data.user);
        else forgetRememberedAccount();
        completeLogin(data);
        return;
      }
      gateAfterRejection();
      setGoogleError(describeAuthFailure(
        response,
        data,
        'This Google account is not authorized for Owaza Admin.',
      ));
    } catch (err) {
      console.warn('Admin Google sign-in request failed:', err?.message || 'network error');
      if (mountedRef.current) setGoogleError(NETWORK_FAILURE);
    } finally {
      authInFlightRef.current = false;
      if (mountedRef.current) setGoogleLoading(false);
    }
  };

  const handleGoogleError = () => {
    setPromptSpent(true);
    setGoogleError({
      title: 'Google sign-in did not finish',
      body: 'Choose Continue with Google to try again.',
    });
  };

  // Only FedCM-compatible moment methods are used; the display-moment status
  // methods are deprecated under FedCM.
  const handlePromptMoment = (notification) => {
    const momentType = typeof notification?.getMomentType === 'function'
      ? notification.getMomentType()
      : null;
    if (momentType === 'display') {
      setPromptStatus('displayed');
      return;
    }
    if (momentType === 'skipped') {
      setPromptStatus('skipped');
      setPromptSpent(true);
      return;
    }
    if (momentType === 'dismissed') {
      const reason = typeof notification.getDismissedReason === 'function'
        ? notification.getDismissedReason()
        : null;
      if (reason !== 'credential_returned') {
        setPromptStatus('dismissed');
        setPromptSpent(true);
      }
    }
  };

  const clearRememberedAccount = () => {
    forgetRememberedAccount();
    disableGoogleAutoSelect();
    setRememberedAccount(null);
    setPromptSpent(true);
    setGoogleError(null);
  };

  const handleUseAnotherAccount = () => {
    clearRememberedAccount();
    setAccountNotice('Choose Continue with Google, then pick the account you want to use.');
  };

  const handleForgetAccount = () => {
    clearRememberedAccount();
    setRememberChoice(false);
    setAccountNotice('This computer no longer remembers your Google account.');
  };

  const handlePasswordSubmit = async (event) => {
    event.preventDefault();
    if (authInFlightRef.current) return;
    const username = localUsername.trim();
    const errors = {};
    if (!username) errors.username = 'Enter your username.';
    if (!localPassword) errors.password = 'Enter your password.';
    setFieldErrors(errors);
    setPasswordError(null);
    if (errors.username) {
      usernameRef.current?.focus();
      return;
    }
    if (errors.password) {
      passwordRef.current?.focus();
      return;
    }

    authInFlightRef.current = true;
    setPromptSpent(true);
    setPasswordLoading(true);
    setGoogleError(null);
    setSessionReason(null);

    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/auth/password`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          username,
          password: localPassword,
        }),
      });
      const data = await readJson(response);

      if (!mountedRef.current) return;
      if (response.ok && data?.token && data?.user) {
        completeLogin(data);
        return;
      }
      gateAfterRejection();
      setPasswordError(describeAuthFailure(
        response,
        data,
        'Check your username and password and try again.',
      ));
      setLocalPassword('');
      passwordRef.current?.focus();
    } catch (err) {
      console.warn('Admin password sign-in request failed:', err?.message || 'network error');
      if (mountedRef.current) setPasswordError(NETWORK_FAILURE);
    } finally {
      authInFlightRef.current = false;
      if (mountedRef.current) setPasswordLoading(false);
    }
  };

  const sessionMessage = sessionReason ? SESSION_MESSAGES[sessionReason] : null;
  const inputClass = (invalid) => (
    `w-full h-11 rounded-md border bg-white dark:bg-gray-900 px-3 text-base sm:text-sm text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-red-500 ${
      invalid ? 'border-red-500' : 'border-gray-300 dark:border-gray-600'
    }`
  );
  const linkButtonClass = 'min-h-[44px] text-gray-700 dark:text-gray-300 underline underline-offset-2 hover:text-gray-900 dark:hover:text-white disabled:opacity-60';

  return (
    <div className="min-h-screen flex items-center justify-center p-4 bg-gradient-to-br from-gray-50 to-gray-100 dark:from-gray-900 dark:to-gray-800">
      <Card className="w-full max-w-md shadow-xl bg-background dark:bg-gray-800 border dark:border-gray-700">
        <CardHeader className="text-center space-y-4">
          <div className="mx-auto w-16 h-16 bg-gradient-to-br from-red-500 to-red-700 rounded-full flex items-center justify-center shadow-lg">
            <Shield className="h-8 w-8 text-white" aria-hidden="true" />
          </div>
          <div>
            <h1 className="text-3xl font-bold leading-none tracking-tight text-gray-900 dark:text-white">Admin Access</h1>
            <p className="text-gray-600 dark:text-gray-400 mt-2">Owaza Martial Arts</p>
          </div>
        </CardHeader>

        <CardContent className="space-y-5">
          {sessionMessage && (
            <Notice
              tone={sessionMessage.tone || 'warning'}
              icon={sessionMessage.icon}
              title={sessionMessage.title}
              testId="admin-login-session-notice"
            >
              {sessionMessage.body}
            </Notice>
          )}

          <section aria-labelledby={googleHeadingId} className="space-y-4">
            <h2 id={googleHeadingId} className="text-center text-sm text-gray-700 dark:text-gray-300">
              {rememberedAccount ? 'Welcome back' : 'Sign in with your authorized Google account'}
            </h2>

            {rememberedAccount && (
              <div
                className="flex items-center gap-3 rounded-lg border border-gray-200 dark:border-gray-700 px-3 py-2"
                data-testid="admin-login-remembered-account"
              >
                <UserRound className="h-5 w-5 flex-shrink-0 text-gray-500" aria-hidden="true" />
                <div className="min-w-0 text-left">
                  {rememberedAccount.name && (
                    <p className="truncate text-sm font-medium text-gray-900 dark:text-gray-100">{rememberedAccount.name}</p>
                  )}
                  <p className="truncate text-sm text-gray-600 dark:text-gray-400">{rememberedAccount.email}</p>
                </div>
              </div>
            )}

            {googleError && (
              <Notice tone="danger" icon={Lock} title={googleError.title} role="alert" testId="admin-login-google-error">
                {googleError.body}
              </Notice>
            )}

            <div
              className="flex min-h-[44px] justify-center"
              data-testid="admin-login-google"
              data-google-prompt={promptStatus}
              data-google-auto-select={autoSelect ? 'on' : 'off'}
            >
              {googleLoading ? (
                <div role="status" className="flex items-center space-x-2 text-gray-600 dark:text-gray-400">
                  <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-red-600" aria-hidden="true"></div>
                  <span>Verifying access...</span>
                </div>
              ) : googleUnavailable ? (
                <Notice tone="warning" icon={AlertTriangle} title="Google sign-in is unavailable" testId="admin-login-google-unavailable">
                  <p>Your network or browser may be blocking Google right now.</p>
                  <button
                    type="button"
                    onClick={() => window.location.reload()}
                    className="mt-1 min-h-[44px] font-semibold underline underline-offset-2"
                  >
                    Try again
                  </button>
                </Notice>
              ) : (
                <GoogleLogin
                  // Remount when the continuation policy changes: the library only
                  // re-initializes Google Identity Services for a subset of props.
                  key={`${promptEnabled}-${autoSelect}-${loginHint || ''}`}
                  onSuccess={handleGoogleSuccess}
                  onError={handleGoogleError}
                  useOneTap={promptEnabled}
                  auto_select={autoSelect}
                  login_hint={loginHint}
                  use_fedcm_for_prompt
                  itp_support
                  promptMomentNotification={handlePromptMoment}
                  theme="filled_blue"
                  size="large"
                  text="continue_with"
                  shape="rectangular"
                  width={300}
                />
              )}
            </div>

            <div className="flex items-start justify-center gap-3">
              <input
                id={rememberId}
                type="checkbox"
                checked={rememberChoice}
                onChange={(event) => setRememberChoice(event.target.checked)}
                disabled={busy}
                aria-describedby={`${rememberId}-hint`}
                className="mt-0.5 h-5 w-5 flex-shrink-0 accent-red-700"
              />
              <div className="text-left">
                <label htmlFor={rememberId} className="text-sm font-medium text-gray-800 dark:text-gray-200">
                  Remember this account on this computer
                </label>
                <p id={`${rememberId}-hint`} className="text-xs text-gray-500 dark:text-gray-400">
                  Leave unchecked on a shared device. Only your email is saved, never a password or sign-in token.
                </p>
              </div>
            </div>

            {rememberedAccount && (
              <div className="flex flex-wrap justify-center gap-x-4 text-sm">
                <button type="button" onClick={handleUseAnotherAccount} disabled={busy} className={linkButtonClass}>
                  Use another Google account
                </button>
                <button type="button" onClick={handleForgetAccount} disabled={busy} className={linkButtonClass}>
                  Forget this account on this computer
                </button>
              </div>
            )}

            <p role="status" className="text-center text-sm text-gray-600 dark:text-gray-400">
              {accountNotice}
            </p>
          </section>

          <div className="border-t border-gray-200 dark:border-gray-700 pt-3">
            <button
              type="button"
              aria-expanded={alternateOpen}
              aria-controls={alternatePanelId}
              onClick={() => setAlternateOpen((open) => !open)}
              className="flex w-full min-h-[44px] items-center justify-between rounded-md px-2 text-sm font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700/50 focus:outline-none focus-visible:ring-2 focus-visible:ring-red-500"
            >
              Use username and password
              <ChevronDown
                className={`h-4 w-4 transition-transform ${alternateOpen ? 'rotate-180' : ''}`}
                aria-hidden="true"
              />
            </button>

            <div id={alternatePanelId} hidden={!alternateOpen} className="pt-3">
              {alternateOpen && (
                <form
                  onSubmit={handlePasswordSubmit}
                  noValidate
                  aria-label="Alternate administrator sign-in"
                  aria-busy={passwordLoading}
                  className="space-y-3"
                >
                  <p className="text-xs text-gray-500 dark:text-gray-400">
                    For administrator accounts that Owaza issued a username.
                  </p>
                  {passwordError && (
                    <Notice tone="danger" icon={Lock} title={passwordError.title} role="alert" testId="admin-login-password-error">
                      {passwordError.body}
                    </Notice>
                  )}
                  <div className="space-y-1">
                    <label htmlFor={usernameId} className="block text-sm font-medium text-gray-800 dark:text-gray-200">
                      Username
                    </label>
                    <input
                      ref={usernameRef}
                      id={usernameId}
                      name="username"
                      type="text"
                      autoComplete="username"
                      autoCapitalize="none"
                      autoCorrect="off"
                      spellCheck={false}
                      value={localUsername}
                      onChange={(event) => setLocalUsername(event.target.value)}
                      readOnly={passwordLoading}
                      aria-invalid={Boolean(fieldErrors.username)}
                      aria-describedby={fieldErrors.username ? `${usernameId}-error` : undefined}
                      className={inputClass(fieldErrors.username)}
                    />
                    {fieldErrors.username && (
                      <p id={`${usernameId}-error`} className="text-sm text-red-700 dark:text-red-300">{fieldErrors.username}</p>
                    )}
                  </div>
                  <div className="space-y-1">
                    <label htmlFor={passwordId} className="block text-sm font-medium text-gray-800 dark:text-gray-200">
                      Password
                    </label>
                    <input
                      ref={passwordRef}
                      id={passwordId}
                      name="password"
                      type="password"
                      autoComplete="current-password"
                      value={localPassword}
                      onChange={(event) => setLocalPassword(event.target.value)}
                      readOnly={passwordLoading}
                      aria-invalid={Boolean(fieldErrors.password)}
                      aria-describedby={fieldErrors.password ? `${passwordId}-error` : undefined}
                      className={inputClass(fieldErrors.password)}
                    />
                    {fieldErrors.password && (
                      <p id={`${passwordId}-error`} className="text-sm text-red-700 dark:text-red-300">{fieldErrors.password}</p>
                    )}
                  </div>
                  <button
                    type="submit"
                    disabled={busy}
                    className="w-full h-11 rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 text-sm font-semibold text-gray-900 dark:text-gray-100 hover:bg-gray-50 dark:hover:bg-gray-800 disabled:cursor-not-allowed disabled:opacity-60 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-red-500"
                  >
                    {passwordLoading ? 'Signing in...' : 'Sign in with password'}
                  </button>
                </form>
              )}
            </div>
          </div>

          <p className="flex items-start gap-2 text-xs text-gray-500 dark:text-gray-400">
            <Lock className="h-4 w-4 mt-0.5 flex-shrink-0" aria-hidden="true" />
            <span>
              Only registered administrators can access this panel. Contact your system administrator if you need access.
            </span>
          </p>
        </CardContent>
      </Card>
    </div>
  );
};

export default AdminLogin;
